import React, {FC} from "react";
import {Button} from "antd";
import {EnvironmentOutlined, RightOutlined} from "@ant-design/icons";
import {groupLessonsByDay, formatGroupHeader, formatCardDate, formatTimeRange} from "./assistantDateUtils";
import {GetCourseLessonsForAssistantAppQuery} from "../../generated/graphql";
import styles from "./assistantApp.less";

type AssistantLesson = GetCourseLessonsForAssistantAppQuery["getCourseLessonsForAssistantApp"][number];

export type AssistantLessonViewProps = {
    lessons: AssistantLesson[];
    pendingLessonId: string | null;
    onAssign: (courseLessonId: string) => void;
    onUnassign: (courseLessonId: string) => void;
};

/**
 * Renders assistant lessons as large card tiles grouped by date.
 * Each card shows course info, date/time, location, instructors,
 * assigned assistants, and an assign/unassign action.
 */
const AssistantLargeTiles: FC<AssistantLessonViewProps> = ({lessons, pendingLessonId, onAssign, onUnassign}) => {
    const dayGroups = groupLessonsByDay(lessons);

    return (
        <>
            {dayGroups.map((group) => (
                <div key={group.date.toISOString()} className={styles.assistantDateGroup}>
                    <div className={styles.assistantDateGroupHeader}>{formatGroupHeader(group.date)}</div>
                    {group.lessons.map((item) => {
                        const lesson = item.courseLesson;
                        const courseLabel = [lesson.course?.courseType?.name, lesson.course?.prefixedCourseNumber].filter(Boolean).join(" · ");
                        const locationParts = [lesson.location?.name, lesson.location?.city].filter(Boolean);
                        const locationLabel = locationParts.join(", ");
                        const venueLabel = lesson.venue?.name || "";
                        const timeRange = formatTimeRange(lesson.startDateTime, lesson.endDateTime);
                        const maxSlots = item.assistantSlotsMax && item.assistantSlotsMax > 0 ? item.assistantSlotsMax : null;
                        const isFull = maxSlots !== null && item.assistantSlotsUsed >= maxSlots;
                        const actionDisabled = !item.isAssignedToMe && isFull;

                        return (
                            <div key={lesson.id} className={`${styles.assistantLgCard}${item.isAssignedToMe ? ` ${styles.assistantLgCardAssigned}` : ""}`}>
                                {/* Header: Kurslabel + Button */}
                                <div className={styles.assistantLgCardHeader}>
                                    <span className={styles.assistantLgCardCourseLabel}>{courseLabel || "Kurseinheit"}</span>
                                    <Button
                                        type={item.isAssignedToMe ? "default" : "primary"}
                                        size="small"
                                        disabled={actionDisabled}
                                        loading={pendingLessonId === lesson.id}
                                        className={item.isAssignedToMe ? styles.assistantLgCardBtnUnassign : styles.assistantLgCardBtnAssign}
                                        onClick={() => {
                                            if (item.isAssignedToMe) {
                                                onUnassign(lesson.id);

                                                return;
                                            }
                                            onAssign(lesson.id);
                                        }}
                                    >
                                        {item.isAssignedToMe ? "Austragen" : "Eintragen"}
                                    </Button>
                                </div>

                                {/* Großes Datum */}
                                <div className={styles.assistantLgCardBigDate}>{formatCardDate(new Date(lesson.startDateTime))}</div>

                                {/* Uhrzeit orange */}
                                <div className={styles.assistantLgCardTime}>
                                    <span className={styles.assistantLgCardTimeDot}>{"●"}</span> {timeRange}
                                    {" Uhr"}
                                </div>

                                {/* Location + Personen-Sektion */}
                                <div className={styles.assistantLgCardLocationSection}>
                                    {locationLabel || venueLabel ? (
                                        <div className={styles.assistantLgCardLocationRow}>
                                            <EnvironmentOutlined className={styles.assistantLgCardLocationPin} />
                                            <span className={styles.assistantLgCardLocationText}>{[locationLabel, venueLabel].filter(Boolean).join(" · ")}</span>
                                            <RightOutlined className={styles.assistantLgCardLocationArrow} />
                                        </div>
                                    ) : null}

                                    {/* KL/AS Tags */}
                                    <div className={styles.assistantLgCardPersonTags}>
                                        {lesson.instructors.map((instructor) => {
                                            const name = [instructor.firstname, instructor.lastname].filter(Boolean).join(" ");
                                            return (
                                                <span key={instructor.id} className={`${styles.assistantLgCardPersonTag} ${styles.assistantLgCardPersonTagKl}`}>
                                                    <span className={`${styles.assistantLgCardPersonBadge} ${styles.assistantLgCardPersonBadgeKl}`}>{"KL"}</span>
                                                    {name}
                                                </span>
                                            );
                                        })}
                                        {item.assignedAssistants && item.assignedAssistants.length > 0
                                            ? item.assignedAssistants.map((a, idx) => {
                                                  const name = [a.firstname, a.lastname].filter(Boolean).join(" ");
                                                  return (
                                                      <span key={`as-${idx}`} className={`${styles.assistantLgCardPersonTag} ${styles.assistantLgCardPersonTagAs}`}>
                                                          <span className={`${styles.assistantLgCardPersonBadge} ${styles.assistantLgCardPersonBadgeAs}`}>{"AS"}</span>
                                                          {name}
                                                      </span>
                                                  );
                                              })
                                            : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </>
    );
};

export default AssistantLargeTiles;
